import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "@/shared/components/ui/Modal";
import { Button } from "@/shared/components/ui/Button";
import { Input } from "@/shared/components/ui/Input";
import { createSubtask, updateTask } from "@/features/tasks/services/taskService";
import type { Task, Subtask, TaskUserSearchResult } from "@/features/tasks/types/task";

interface SubtaskFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  onError?: (err: any) => void;
  parentTask: Task;
  /** When set, the form is in edit mode for this subtask. */
  subtask?: Subtask | null;
  /**
   * Used in create mode — the shared member list selected once in TaskDetailPanel.
   * Shown as read-only; not needed for edit mode.
   */
  fixedAssignees?: TaskUserSearchResult[];
}

export const SubtaskFormModal: React.FC<SubtaskFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  onError,
  parentTask,
  subtask = null,
  fixedAssignees,
}) => {
  const { t } = useTranslation();
  const isEdit = !!subtask;

  const [title, setTitle]             = useState("");
  const [description, setDescription] = useState("");
  const [deadline, setDeadline]       = useState("");
  const [titleError, setTitleError]   = useState("");
  const [saving, setSaving]           = useState(false);

  // Populate form when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setTitleError("");
    if (subtask) {
      setTitle(subtask.title);
      setDescription(subtask.description ?? "");
      setDeadline(subtask.deadline ? subtask.deadline.split("T")[0] : "");
    } else {
      setTitle("");
      setDescription("");
      setDeadline("");
    }
  }, [isOpen, subtask]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setTitleError(t("tasks.form.field_title_required"));
      return;
    }

    // Create mode requires at least one assignee from the shared list
    if (!isEdit && (fixedAssignees ?? []).length === 0) return;

    setTitleError("");
    setSaving(true);
    try {
      if (isEdit && subtask) {
        await updateTask(subtask.id, {
          title:        title.trim(),
          description:  description.trim() || undefined,
          deadline:     deadline || undefined,
          // Keep existing assignees unchanged
          assignee_ids: subtask.assignees.map((a) => a.user_id),
        });
      } else {
        await createSubtask(parentTask.id, {
          title:        title.trim(),
          description:  description.trim() || undefined,
          deadline:     deadline || undefined,
          assignee_ids: (fixedAssignees ?? []).map((u) => u.user_id),
        });
      }
      onSaved();
      onClose();
    } catch (err: any) {
      console.error("Failed to save subtask", err);
      onError?.(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t("tasks.subtasks.edit_subtask") : t("tasks.subtasks.add_subtask")}
      size="md"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">

        {/* Parent context */}
        <div className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
          {t("tasks.subtasks.under_parent")}:{" "}
          <span className="font-medium text-gray-700">{parentTask.title}</span>
        </div>

        {/* Title */}
        <Input
          label={t("tasks.form.field_title")}
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            if (e.target.value.trim()) setTitleError("");
          }}
          placeholder={t("tasks.form.field_title_placeholder")}
          error={titleError}
          required
        />

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("tasks.form.field_description")}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("tasks.form.field_description_placeholder")}
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>

        {/* Deadline */}
        <Input
          label={t("tasks.form.field_deadline")}
          type="date"
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
        />

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose}>
            {t("tasks.form.cancel")}
          </Button>
          <Button
            type="submit"
            loading={saving}
            disabled={!isEdit && (fixedAssignees ?? []).length === 0}
          >
            {saving
              ? t("tasks.form.saving")
              : isEdit
              ? t("tasks.form.save")
              : t("tasks.subtasks.create_subtask")}
          </Button>
        </div>

      </form>
    </Modal>
  );
};
